import type { ElementType } from 'react';
import type { UserLocalStorage } from '../../types/localStorage';

import { createElement, use, useCallback, useEffect, useMemo } from 'react';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useLocalStorageState from 'use-local-storage-state';

import { getEnv } from '../../lib/env';
import { putUserConfig } from '../../lib/flotiq-client';
import { markTaskAsDone } from '../../lib/flotiq-client/api-helpers';
import FlotiqPlugins from '../../lib/flotiq-plugins/flotiqPluginsRegistry';
import { generateApiDocs } from '../../lib/helpers';

import Loader from '../../components/Loader/Loader';

import UserContext from '../../contexts/UserContext';

import {
  ClipboardColorIcon,
  ClipboardColorIconWhite,
  ConnectedDotsColorIcon,
  ConnectedDotsColorIconWhite,
  CopyColorIcon,
  CopyColorIconWhite,
  FolderIcon,
  FolderIconWhite,
  FourSquaresColor,
  FourSquaresColorWhite,
  GearBoxColorIcon,
  GearBoxIconWhite,
  GearColorIcon,
  GearColorIconWhite,
  GraphQLLogo,
  KeyColorIcon,
  KeyColorIconWhite,
  LightningColorIcon,
  LightningColorIconWhite,
  MediaColorIcon,
  MediaColorIconWhite,
  MenuIcon,
  MenuIconWhite,
  PuzzleColorIcon,
  PuzzleColorIconWhite,
  StarFavIcon,
  StarFavWhiteIcon,
  TagsColorIcon,
  TagsColorIconWhite,
  UsersIcon,
  UsersKeyIcon,
  UsersKeyIconWhite,
} from '../../images/shapes';

import { useApiKeys, useContentTypes, useUser } from '../api';
import useApiErrorsToast from '../api/useApiErrorsToast';
import useDarkMode from '../useDarkMode';
import useSelectedSpace from '../useSelectedSpace';
import useToken from '../useToken';
import {
  getUserPreferences,
  hasHiddenData,
  isViewOverridden,
} from './UISettings/uiSettings';

const APIKEY_PARAMS = { name: 'read only api key' };

const CTD_PARAMS = {
  internal: 0,
  limit: 1000,
  order_by: 'label',
  order_direction: 'asc',
  page: 1,
};

type PinedElement =
  | {
      key: string;
      name: string;
      title: string;
      children: PinedElement[];
    }
  | {
      key: string;
      name: string;
      id: string;
      title: string;
      link: string;
      pined?: boolean;
    };

export const useSpaceMenuItems = () => {
  const { t } = useTranslation();
  const jwt = useToken();
  const navigate = useNavigate();

  const [userLS, setUserLS] =
    useLocalStorageState<UserLocalStorage>('cms.user');
  const [user] = useUser(userLS?.data?.id);
  const { isRoleAdmin, permissions, isAdmin } = use(UserContext);

  const [darkMode] = useDarkMode();
  const { space, buildUrlWithSpace, spaceLimits } = useSelectedSpace();

  const { data: apiKeysData } = useApiKeys(APIKEY_PARAMS);
  const {
    data: contentTypes,
    errors: ctdErrors,
    isLoading: ctdLoading,
  } = useContentTypes(CTD_PARAMS);

  useApiErrorsToast(ctdErrors);

  const isOverridden = useMemo(() => isViewOverridden(userLS), [userLS]);

  const savePreferences = useCallback(async () => {
    if (!userLS?.data) return;

    const newConfig = { ...(userLS.data.config || {}) };

    if (!newConfig.viewPreferences) {
      newConfig.viewPreferences = getUserPreferences(userLS);
    } else {
      newConfig.viewPreferences.isOverridden = !isOverridden;
    }

    putUserConfig(jwt, undefined, {
      id: userLS.data.id,
      config: newConfig,
    }).catch(() => {
      //do nothing and update config for current session
    });

    const newUser = { ...userLS };
    newUser.data.config = newConfig;

    setUserLS(newUser);
  }, [isOverridden, jwt, setUserLS, userLS]);

  useEffect(() => {
    if (!userLS?.data) return;
    if (userLS.data.workType && !userLS.data.config?.viewPreferences) {
      savePreferences();
    }
  }, [savePreferences, userLS?.data]);

  const hasPermissions = useCallback(
    (pageKey: string) => {
      switch (pageKey) {
        case 'content':
        case 'favorites':
          return permissions?.cosVisible();
        case 'media':
          return permissions?.canCo('_media');
        case 'tags':
          return permissions?.canCo('_tag');
        case 'definitionBuilder':
          return permissions?.ctdsVisible();
        case 'webhooks':
          return permissions?.canCo('_webhooks');
        case 'plugins':
          return permissions?.canCo('_plugin_settings');
        case 'userRoles':
          return (
            isAdmin &&
            (!!spaceLimits?.user_roles_enabled ||
              !!spaceLimits?.predefined_user_roles) &&
            getEnv('VITE_ENABLE_USER_ROLES') === 'true'
          );
        case 'admin': {
          return isRoleAdmin;
        }
        case 'starters': {
          return !!getEnv('VITE_FLOTIQ_STARTERS_URL');
        }
        case 'apiDocs': {
          return !!getEnv('VITE_API_DOC_GENERATOR_URL');
        }
        case 'togglePreferences':
          return hasHiddenData(userLS);

        default:
          return true;
      }
    },
    [
      isAdmin,
      isRoleAdmin,
      permissions,
      spaceLimits?.predefined_user_roles,
      spaceLimits?.user_roles_enabled,
      userLS,
    ],
  );

  const [contentItems, pinedItems] = useMemo(() => {
    if (ctdLoading)
      return [
        [
          {
            key: 'loading',
            title: <Loader size="tiny" type="spinner-grid" />,
            disabled: true,
          },
        ],
        [],
      ];

    if (!contentTypes) return [[], []];

    const pinedElements: PinedElement[] = [];

    const dict = contentTypes.reduce(
      (folders, el) => {
        if (!permissions?.canCo(el.name)) return folders;
        const hasFolder = el.label.includes('/');
        const [splitFolderName, ...subfolderNameParts] =
          el.label?.split('/') || [];
        const folderName = hasFolder ? splitFolderName : '';
        const splitLabel = hasFolder ? subfolderNameParts.join('/') : el.label;

        const pinedContent = userLS?.data?.config?.pinedDefinitions;
        const isPinned = !space
          ? false
          : pinedContent?.[space]?.includes(el.id);

        const menuElement = {
          key: splitLabel,
          name: el.name,
          id: el.id,
          title: splitLabel || el.label,
          link: buildUrlWithSpace(`content-type-objects/${el.name}`),
          pined: isPinned,
        };

        if (isPinned) {
          pinedElements.push({
            ...menuElement,
            title: el.label,
            link: buildUrlWithSpace(
              `content-type-objects/${el.name}?favorites=1`,
            ),
            id: el.id,
            key: `${el.label}-pinned`,
          });
        }

        if (folders[folderName]) folders[folderName].push(menuElement);
        else folders[folderName] = [menuElement];
        return folders;
      },
      {} as Record<string, PinedElement[]>,
    );
    const items: PinedElement[] = [];

    Object.keys(dict)
      .sort((a, b) => {
        if (!a) return 1;
        if (!b) return -1;

        return a > b ? 1 : -1;
      })
      .forEach((key) => {
        if (key === '') items.push(...dict[key]);
        else {
          items.push({
            key: key,
            name: key,
            title: key,
            children: dict[key],
          });
        }
      });

    return [items || [], pinedElements || []];
  }, [
    ctdLoading,
    contentTypes,
    permissions,
    userLS?.data?.config?.pinedDefinitions,
    space,
    buildUrlWithSpace,
  ]);

  const selectedSpaceHighlight =
    "after:content-['•'] after:text-lg after:text-blue after:text-3xl after:absolute " +
    'after:top-1/2 after:-translate-y-1/2 after:right-[5px] after:leading-none font-medium';

  const menuSpaceContentItems = useMemo(() => {
    if (!user) return undefined;

    const spacesMenuOptions = [];

    if (user?.spaces?.length) {
      const userSpaces = user.spaces.map((userSpace) => {
        const isSelected = userSpace.id === space;
        return {
          ...userSpace,
          key: userSpace.id,
          title: userSpace.name,
          additionalClasses: isSelected ? selectedSpaceHighlight : '',
          onClick: () => {
            navigate(`/s/${userSpace.slug}`);
            if (!isSelected) FlotiqPlugins.unregisterAllPlugins();
          },
        };
      });
      spacesMenuOptions.push(...userSpaces);
    }

    spacesMenuOptions.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });

    if (isAdmin) {
      spacesMenuOptions.push({
        key: 'manage-spaces',
        id: '_manage-spaces',
        title: t('Spaces.ManageSpaces'),
        value: 'manage-spaces',
        link: '/organization',
        additionalClasses: 'text-blue-600 underline',
      });
      spacesMenuOptions.push({
        key: 'add-space',
        id: '_add-space',
        title: t('Spaces.PlusAddSpace'),
        value: 'manage-spaces',
        link: '/space/add',
        additionalClasses: 'text-blue-600 underline',
      });
    }

    return spacesMenuOptions;
  }, [user, isAdmin, space, selectedSpaceHighlight, navigate, t]);

  const icon = useCallback(
    (Light: ElementType, Dark: ElementType) =>
      createElement(darkMode ? Dark : Light, { className: 'w-6 min-w-6' }),
    [darkMode],
  );

  const menuItemProps = useMemo(
    () => [
      {
        key: 'spaces',
        icon: icon(FolderIcon, FolderIconWhite),
        title:
          menuSpaceContentItems?.filter((el) => el.id === space)?.[0]?.title ||
          '',
        hidePin: true,
        children: menuSpaceContentItems,
        titleClassName: 'font-bold',
        className: `h-10 ${
          !menuSpaceContentItems?.length && 'pointer-events-none'
        }`,
        popover: true,
      },
      {
        key: 'dashboard',
        icon: icon(FourSquaresColor, FourSquaresColorWhite),
        title: t('Global.Dashboard'),
        link: buildUrlWithSpace(''),
      },
      {
        key: 'favorites',
        icon: icon(StarFavIcon, StarFavWhiteIcon),
        title: t('Global.Favorite'),
        ...(!pinedItems.length &&
          !ctdLoading && {
            tooltip: t('Global.FavoriteEmptyTooltip'),
            className: 'opacity-50',
          }),
        children: pinedItems,
      },
      {
        key: 'media',
        icon: icon(MediaColorIcon, MediaColorIconWhite),
        title: t('Global.MediaLibrary'),
        link: buildUrlWithSpace('media'),
      },
      {
        key: 'tags',
        icon: icon(TagsColorIcon, TagsColorIconWhite),
        title: t('Global.Tags'),
        link: buildUrlWithSpace('content-type-objects/_tag'),
      },
      {
        key: 'userRoles',
        icon: icon(UsersKeyIcon, UsersKeyIconWhite),
        title: t('Global.UserRoles'),
        link: buildUrlWithSpace('user-roles'),
      },
      {
        key: 'content',
        icon: icon(ClipboardColorIcon, ClipboardColorIconWhite),
        title: t('Global.Content'),
        hidePin: ctdLoading,
        ...(contentItems.length
          ? { children: contentItems }
          : { link: buildUrlWithSpace('content') }),
      },
      {
        key: 'definitionBuilder',
        icon: icon(GearColorIcon, GearColorIconWhite),
        title: t('Global.DefinitionBuilder'),
        link: buildUrlWithSpace('content-type-definitions'),
      },
      {
        key: 'webhooks',
        icon: icon(ConnectedDotsColorIcon, ConnectedDotsColorIconWhite),
        title: t('Global.Webhooks'),
        link: buildUrlWithSpace('webhooks'),
      },
      {
        key: 'plugins',
        icon: icon(PuzzleColorIcon, PuzzleColorIconWhite),
        title: t('Global.Plugins'),
        link: buildUrlWithSpace('plugins'),
      },
      {
        key: 'starters',
        icon: icon(LightningColorIcon, LightningColorIconWhite),
        title: t('Global.Starters'),
        link: buildUrlWithSpace('starters'),
      },
      {
        key: 'apiKeys',
        icon: icon(KeyColorIcon, KeyColorIconWhite),
        title: t('Global.APIKeys'),
        link: buildUrlWithSpace('api-keys'),
      },
      {
        key: 'apiDocs',
        icon: icon(CopyColorIcon, CopyColorIconWhite),
        title: t('Global.APIDocs'),
        link: generateApiDocs(userLS?.data, apiKeysData?.[0]),
        target: '_blank',
        rel: 'noreferrer',
        onClick: () => {
          markTaskAsDone('publicDoc', user, jwt, setUserLS);
          TagManager.dataLayer({
            dataLayer: {
              event: 'apidocs_visit',
            },
          });
        },
      },
      ...(getEnv('VITE_GRAPHQL_EXPLORER_URL')
        ? [
            {
              key: 'graphqlExplorer',
              icon: (
                <GraphQLLogo className="w-6 min-w-6 dark:text-slate-200 text-slate-900" />
              ),
              title: t('Global.GraphQLExplorer'),
              link: `${getEnv('VITE_GRAPHQL_EXPLORER_URL')}?auth_token=${apiKeysData?.[0]?.apiKey}`,
              target: '_blank',
              rel: 'noreferrer',
              onClick: () => {
                TagManager.dataLayer({
                  dataLayer: {
                    event: 'graphql_explorer_visit',
                  },
                });
              },
            },
          ]
        : []),
      {
        key: 'admin',
        className: 'border-t border-slate-100 dark:border-slate-800 mt-1.5',
        icon: <StarFavIcon className="w-6 min-w-6" />,
        title: 'Admin',
        hidePin: true,
        children: [
          {
            key: 'all-users',
            icon: <UsersIcon className="w-6 min-w-6" />,
            title: t('Global.AllUsers'),
            link: '/users-data-preview',
          },
          {
            key: 'all-spaces',
            icon: <UsersIcon className="w-6 min-w-6" />,
            title: t('Global.AllSpaces'),
            link: '/spaces-data-preview',
          },
          {
            key: 'plans',
            icon: <UsersIcon className="w-6 min-w-6" />,
            title: t('Global.Plans'),
            link: '/plans',
          },
        ],
      },
      {
        key: 'togglePreferences',
        className:
          'border-t border-slate-100 dark:border-slate-800 text-[15px] min-h-[50px]',
        titleClassName: 'leading-4 line-clamp-2',
        icon: icon(MenuIcon, MenuIconWhite),
        title: isOverridden
          ? t('Global.HideAdvancedOptions')
          : t('Global.ShowAdvancedOptions'),
        onClick: savePreferences,
      },
      {
        key: 'spaceSettings',
        icon: icon(GearBoxColorIcon, GearBoxIconWhite),
        title: t('Global.SpaceSettings'),
        link: buildUrlWithSpace('space-settings'),
      },
    ],
    [
      icon,
      menuSpaceContentItems,
      t,
      buildUrlWithSpace,
      pinedItems,
      ctdLoading,
      contentItems,
      userLS?.data,
      apiKeysData,
      isOverridden,
      savePreferences,
      space,
      user,
      jwt,
      setUserLS,
    ],
  );

  const menuItems = useMemo(() => {
    if (!user) return [];
    if (!space)
      return [
        {
          key: 'dashboard',
          icon: darkMode ? (
            <FourSquaresColorWhite className="w-6 min-w-6" />
          ) : (
            <FourSquaresColor className="w-6 min-w-6" />
          ),
          title: t('Global.Dashboard'),
          link: `/`,
        },
      ];

    const viewPreferences = getUserPreferences(userLS);
    return menuItemProps.filter(
      ({ key }) =>
        hasPermissions(key) &&
        viewPreferences.navigation?.[
          key as keyof typeof viewPreferences.navigation
        ] !== false,
    );
  }, [darkMode, hasPermissions, menuItemProps, space, t, user, userLS]);

  return { menuItems };
};
